import type {
  CashflowBuilderConfig,
  CashflowRowMeta,
  CashflowRule,
  CashflowTemplateRow,
  FormulaDefinition,
} from "../types";

import {
  cashflowTemplateRows,
  defaultConfig,
  defaultRowMeta,
} from "./cashflowTemplate";

const STORAGE_KEY = "cashflow-query-builder/v1";

function cloneDefaultConfig(): CashflowBuilderConfig {
  return JSON.parse(JSON.stringify(defaultConfig)) as CashflowBuilderConfig;
}

function isFormulaDefinition(value: unknown): value is FormulaDefinition {
  if (!value || typeof value !== "object") {
    return false;
  }

  const maybe = value as FormulaDefinition;
  return (
    (maybe.kind === "sum" || maybe.kind === "diff") &&
    Array.isArray(maybe.references) &&
    (maybe.stage === 1 || maybe.stage === 2 || maybe.stage === 3) &&
    typeof maybe.description === "string"
  );
}

function isCustomRow(value: unknown): value is CashflowTemplateRow {
  if (!value || typeof value !== "object") {
    return false;
  }

  const maybe = value as CashflowTemplateRow;
  return (
    typeof maybe.rowId === "string" &&
    typeof maybe.displayCode === "string" &&
    typeof maybe.label === "string" &&
    (maybe.rowType === "section" ||
      maybe.rowType === "leaf" ||
      maybe.rowType === "formula") &&
    typeof maybe.order === "number" &&
    typeof maybe.sectionId === "string"
  );
}

function isRule(value: unknown): value is CashflowRule {
  if (!value || typeof value !== "object") {
    return false;
  }

  const maybe = value as CashflowRule;
  
  // Check for new format (dt_match_type and kt_match_type)
  const hasNewFormat = 
    (maybe.dt_match_type === "gl_code" || maybe.dt_match_type === "acct_num") &&
    (maybe.kt_match_type === "gl_code" || maybe.kt_match_type === "acct_num");
  
  // Check for old format (matchType)
  const hasOldFormat = 
    maybe.matchType === "gl_code" || maybe.matchType === "acct_num";
  
  return (
    typeof maybe.id === "string" &&
    typeof maybe.rowId === "string" &&
    (hasNewFormat || hasOldFormat) &&
    typeof maybe.debitValue === "string" &&
    typeof maybe.creditValue === "string" &&
    (maybe.amountSide === "CR" || maybe.amountSide === "DR") &&
    typeof maybe.coefficient === "number"
  );
}

function migrateRule(rule: CashflowRule): CashflowRule {
  // If rule has old matchType but not new fields, migrate it
  if (rule.matchType && (!rule.dt_match_type || !rule.kt_match_type)) {
    return {
      ...rule,
      dt_match_type: rule.matchType,
      kt_match_type: rule.matchType,
    };
  }
  
  // If rule has new fields, ensure they're set
  if (!rule.dt_match_type || !rule.kt_match_type) {
    return {
      ...rule,
      dt_match_type: rule.dt_match_type || "gl_code",
      kt_match_type: rule.kt_match_type || "gl_code",
    };
  }
  
  return rule;
}

function isRowMeta(value: unknown): value is CashflowRowMeta {
  if (!value || typeof value !== "object") {
    return false;
  }

  const maybe = value as CashflowRowMeta;
  return (
    typeof maybe.displayCode === "string" &&
    typeof maybe.label === "string" &&
    typeof maybe.internalName === "string"
  );
}

export function normalizeConfig(input: unknown): CashflowBuilderConfig {
  const normalized = cloneDefaultConfig();

  if (!input || typeof input !== "object") {
    return normalized;
  }

  const parsed = input as Partial<CashflowBuilderConfig>;

  if (typeof parsed.startDate === "string" && parsed.startDate.length > 0) {
    normalized.startDate = parsed.startDate;
  }

  if (typeof parsed.endDate === "string" && parsed.endDate.length > 0) {
    normalized.endDate = parsed.endDate;
  }

  normalized.removedTemplateRowIds = Array.isArray(parsed.removedTemplateRowIds)
    ? parsed.removedTemplateRowIds.filter(
        (rowId): rowId is string =>
          typeof rowId === "string" && !!defaultRowMeta[rowId],
      )
    : [];

  normalized.rowMeta = { ...defaultRowMeta };

  for (const [rowId, meta] of Object.entries(parsed.rowMeta ?? {})) {
    if (!defaultRowMeta[rowId] || !isRowMeta(meta)) {
      continue;
    }

    normalized.rowMeta[rowId] = {
      displayCode: meta.displayCode.trim() || defaultRowMeta[rowId].displayCode,
      label: meta.label.trim() || defaultRowMeta[rowId].label,
      internalName: meta.internalName.trim() || defaultRowMeta[rowId].internalName,
    };
  }

  normalized.customRows = Array.isArray(parsed.customRows)
    ? parsed.customRows.filter(isCustomRow)
    : [];

  normalized.formulas = { ...defaultConfig.formulas };

  for (const [rowId, formula] of Object.entries(parsed.formulas ?? {})) {
    if (isFormulaDefinition(formula)) {
      normalized.formulas[rowId] = formula;
    }
  }

  for (const customRow of normalized.customRows) {
    normalized.rowMeta[customRow.rowId] = {
      displayCode: customRow.displayCode,
      label: customRow.label,
      internalName: customRow.internalName ?? customRow.rowId,
    };

    const formula = parsed.formulas?.[customRow.rowId];
    if (customRow.rowType === "formula" && isFormulaDefinition(formula)) {
      normalized.formulas[customRow.rowId] = formula;
    }
  }

  if (parsed.rows && typeof parsed.rows === "object") {
    for (const [rowId, rawRules] of Object.entries(parsed.rows)) {
      if (!Array.isArray(rawRules)) {
        continue;
      }

      normalized.rows[rowId] = rawRules
        .filter(isRule)
        .filter((rule) => rule.rowId === rowId)
        .map((rule) => migrateRule(rule))
        .map((rule) => ({
          ...rule,
          coefficient: Number.isFinite(rule.coefficient) ? rule.coefficient : 1,
        }));
    }
  }

  return normalized;
}

export function loadConfig(): CashflowBuilderConfig {
  if (typeof window === "undefined") {
    return cloneDefaultConfig();
  }

  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return cloneDefaultConfig();
  }

  try {
    return normalizeConfig(JSON.parse(raw));
  } catch {
    return cloneDefaultConfig();
  }
}

export function saveConfig(config: CashflowBuilderConfig): void {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(config, null, 2));
}

export function exportConfig(config: CashflowBuilderConfig): void {
  const payload = JSON.stringify(config, null, 2);
  const blob = new Blob([payload], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "cashflow-builder-config.json";
  anchor.click();
  URL.revokeObjectURL(url);
}

export async function importConfig(file: File): Promise<CashflowBuilderConfig> {
  const content = await file.text();
  return normalizeConfig(JSON.parse(content));
}

export function resetConfig(): CashflowBuilderConfig {
  return cloneDefaultConfig();
}

export function createEmptyConfig(): CashflowBuilderConfig {
  const next = cloneDefaultConfig();
  next.removedTemplateRowIds = [...cashflowTemplateRows.map((row) => row.rowId)];
  return normalizeConfig(next);
}
